// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorProfilePage
// =============================================================================

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Upload, Loader2, MapPin, Globe, Phone, Mail } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { VerificationStatus } from '@/features/vendor/components/VerificationStatus';
import { ImageUploader } from '@/features/vendor/components/ImageUploader';
import {
  useVendorProfile,
  useUpdateVendorProfile,
  useUploadVendorLogo,
  useUploadVendorBanner,
} from '@/features/vendor/hooks/useVendorProfile';
import { useVendorCategories } from '@/features/vendor/hooks/useVendorProducts';
import { vendorProfileSchema, type VendorProfileFormData } from '@/features/vendor/types/vendorTypes';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export function VendorProfilePage() {
  const { data: profile, isLoading } = useVendorProfile();
  const { data: categories } = useVendorCategories();
  const updateProfile = useUpdateVendorProfile();
  const uploadLogo = useUploadVendorLogo();
  const uploadBanner = useUploadVendorBanner();

  const [activeTab, setActiveTab] = useState<'basic' | 'business' | 'verification'>('basic');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<VendorProfileFormData>({
    resolver: zodResolver(vendorProfileSchema),
    defaultValues: profile
      ? {
          name: profile.name,
          description: profile.description,
          email: profile.email,
          phone: profile.phone,
          website: profile.website || '',
          address: profile.address,
          serviceAreaNational: profile.serviceAreaNational,
          serviceAreaStates: profile.serviceAreaStates,
          yearsInBusiness: profile.yearsInBusiness,
          certifications: profile.certifications,
          insuranceAccepted: profile.insuranceAccepted,
          categoryIds: profile.categoryIds,
        }
      : undefined,
  });

  const serviceAreaNational = watch('serviceAreaNational');

  const onSubmit = async (data: VendorProfileFormData) => {
    await updateProfile.mutateAsync(data);
  };

  const handleLogoUpload = async (files: File[]) => {
    if (files[0]) {
      await uploadLogo.mutateAsync(files[0]);
    }
  };

  const handleBannerUpload = async (files: File[]) => {
    if (files[0]) {
      await uploadBanner.mutateAsync(files[0]);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-warm-gray">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <VendorHeader
        title="Profile Settings"
        subtitle="Manage your vendor profile and business information"
        actions={
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || updateProfile.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white font-medium rounded-xl hover:bg-terracotta-light transition-colors disabled:opacity-50"
          >
            {updateProfile.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save Changes
          </button>
        }
      />

      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-200 w-fit">
          {[
            { id: 'basic', label: 'Basic Info' },
            { id: 'business', label: 'Business Details' },
            { id: 'verification', label: 'Verification' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-terracotta text-white'
                  : 'text-navy hover:bg-cream-dark'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <>
              {/* Logo & Banner */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-6">
                  Branding
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Logo
                    </label>
                    <div className="flex items-center gap-4">
                      {profile.logoUrl ? (
                        <img
                          src={profile.logoUrl}
                          alt="Logo"
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-cream-dark flex items-center justify-center">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <label className="cursor-pointer px-4 py-2 bg-cream text-navy font-medium rounded-lg hover:bg-cream-dark transition-colors inline-block">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleLogoUpload([file]);
                            }}
                          />
                          {uploadLogo.isPending ? 'Uploading...' : 'Upload Logo'}
                        </label>
                        <p className="text-xs text-warm-gray mt-1">
                          Recommended: 200x200px, PNG or JPG
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Banner
                    </label>
                    <div className="space-y-2">
                      {profile.bannerUrl ? (
                        <img
                          src={profile.bannerUrl}
                          alt="Banner"
                          className="w-full h-24 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-full h-24 rounded-xl bg-cream-dark flex items-center justify-center">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <label className="cursor-pointer px-4 py-2 bg-cream text-navy font-medium rounded-lg hover:bg-cream-dark transition-colors inline-block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleBannerUpload([file]);
                          }}
                        />
                        {uploadBanner.isPending ? 'Uploading...' : 'Upload Banner'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Details */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-6">
                  Basic Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy mb-2">
                      Business Name *
                    </label>
                    <input
                      {...register('name')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                    {errors.name && (
                      <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy mb-2">
                      Description *
                    </label>
                    <textarea
                      {...register('description')}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta resize-none"
                    />
                    {errors.description && (
                      <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Phone *
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy mb-2">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Website
                    </label>
                    <input
                      type="url"
                      {...register('website')}
                      placeholder="https://yourwebsite.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-6">
                  <MapPin className="w-5 h-5 inline mr-2" />
                  Address
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy mb-2">
                      Street Address
                    </label>
                    <input
                      {...register('address.street')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">City</label>
                    <input
                      {...register('address.city')}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">State</label>
                      <select
                        {...register('address.state')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta bg-white"
                      >
                        <option value="">Select</option>
                        {US_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">ZIP</label>
                      <input
                        {...register('address.zipCode')}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Business Details Tab */}
          {activeTab === 'business' && (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-6">
                  Business Information
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Years in Business
                    </label>
                    <input
                      type="number"
                      {...register('yearsInBusiness', { valueAsNumber: true })}
                      min="0"
                      max="100"
                      className="w-full max-w-xs px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-3">
                      Categories
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {categories?.map((cat) => (
                        <label
                          key={cat.id}
                          className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-cream-dark"
                        >
                          <input
                            type="checkbox"
                            value={cat.id}
                            {...register('categoryIds')}
                            className="w-4 h-4 rounded border-gray-300 text-terracotta focus:ring-terracotta"
                          />
                          <span className="text-sm">{cat.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-display text-lg font-semibold text-navy mb-6">
                  Service Area
                </h2>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('serviceAreaNational')}
                      className="w-5 h-5 rounded border-gray-300 text-terracotta focus:ring-terracotta"
                    />
                    <span className="font-medium text-navy">Nationwide Service</span>
                  </label>

                  {!serviceAreaNational && (
                    <div>
                      <label className="block text-sm font-medium text-navy mb-3">
                        Select States
                      </label>
                      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                        {US_STATES.map((state) => (
                          <label
                            key={state}
                            className="flex items-center justify-center p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-cream-dark text-sm"
                          >
                            <input
                              type="checkbox"
                              value={state}
                              {...register('serviceAreaStates')}
                              className="sr-only"
                            />
                            {state}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Verification Tab */}
          {activeTab === 'verification' && (
            <VerificationStatus profile={profile} />
          )}
        </form>
      </div>
    </div>
  );
}

export default VendorProfilePage;

